import React, { useCallback, useMemo } from "react";
import { RefreshCw } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  DailyMoodTrendChart,
  buildDailyMoodTrendPoints,
} from "../components/DailyMoodTrendChart";
import {
  SudsTrendChart,
  buildSudsTrendPoints,
} from "../components/SudsTrendChart";
import {
  YouthTopProblemsTrendChart,
  buildYouthTopProblemsTrendPoints,
} from "../components/YouthTopProblemsTrendChart";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";
import { insightsNavigation } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";

export function ClinicalTrendsPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const historyRequest = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            "all-activities",
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            "all-activities",
            signal,
          ),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const moodRequest = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getDailyMoodChecks(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getDailyMoodChecks(clinicianCode, patientId, signal),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const youthTopProblemsRequest = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getYouthTopProblemRatings(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getYouthTopProblemRatings(
            clinicianCode,
            patientId,
            signal,
          ),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const historyState = usePortalData(historyRequest);
  const moodState = usePortalData(moodRequest);
  const youthTopProblemsState = usePortalData(youthTopProblemsRequest);
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const historyData =
    historyState.status === "success" || historyState.status === "refreshing"
      ? historyState.data
      : undefined;
  const moodData =
    moodState.status === "success" || moodState.status === "refreshing"
      ? moodState.data
      : undefined;
  const youthTopProblemsData =
    youthTopProblemsState.status === "success" ||
    youthTopProblemsState.status === "refreshing"
      ? youthTopProblemsState.data
      : undefined;
  const historyAvailable = Boolean(
    historyData?.found &&
      historyData.authorized &&
      historyData.therapist &&
      historyData.patient &&
      historyData.historyFilter === "all-activities",
  );
  const moodAvailable = Boolean(
    moodData?.found &&
      moodData.authorized &&
      moodData.therapist &&
      moodData.patient?.id === patientId,
  );
  const youthTopProblemsAvailable = Boolean(
    youthTopProblemsData?.found &&
      youthTopProblemsData.authorized &&
      youthTopProblemsData.therapist &&
      youthTopProblemsData.patient?.id === patientId &&
      youthTopProblemsData.respondentType === "client",
  );
  const patient = historyAvailable ? historyData?.patient : snapshot?.patient;
  const therapist = historyAvailable
    ? historyData?.therapist
    : snapshot?.therapist;
  const sudsPoints = useMemo(
    () =>
      buildSudsTrendPoints(
        historyAvailable ? historyData?.assignments ?? [] : [],
      ),
    [historyAvailable, historyData?.assignments],
  );
  const moodPoints = useMemo(
    () =>
      buildDailyMoodTrendPoints(
        moodAvailable ? moodData?.moodChecks ?? [] : [],
      ),
    [moodAvailable, moodData?.moodChecks],
  );
  const youthTopProblemsPoints = useMemo(
    () =>
      buildYouthTopProblemsTrendPoints(
        youthTopProblemsAvailable ? youthTopProblemsData?.ratings ?? [] : [],
      ),
    [youthTopProblemsAvailable, youthTopProblemsData?.ratings],
  );
  const refreshing =
    historyState.status === "refreshing" ||
    moodState.status === "refreshing" ||
    youthTopProblemsState.status === "refreshing";
  const refresh = () => {
    historyState.refresh();
    moodState.refresh();
    youthTopProblemsState.refresh();
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Insights",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="insights"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-page-content--compact-client cp-page-content--sticky-secondary-nav">
        <SegmentedNavigation
          items={insightsNavigation}
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="clinical-trends"
        />

        {historyState.status === "loading" ? (
          <LoadingState label="Loading clinical trends" />
        ) : null}
        {historyState.status === "error" ? (
          <ErrorState error={historyState.error} onRetry={refresh} />
        ) : null}
        {historyData && !historyAvailable ? (
          <ErrorState error={new Error("Clinical trends are not available")} />
        ) : null}
        {historyAvailable ? (
          <section aria-labelledby="clinical-trends-heading">
            <div className="cp-content-header cp-content-header--route-intro">
              <div>
                <h2
                  id="clinical-trends-heading"
                  className="cp-visually-hidden"
                >
                  Clinical Trends
                </h2>
                <p>
                  Review distress, daily mood, and youth-reported problem
                  patterns over time.
                </p>
              </div>
              <div className="cp-content-header__actions">
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  disabled={refreshing}
                  onClick={refresh}
                >
                  <RefreshCw aria-hidden="true" />
                  {refreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>

            <div className="cp-clinical-trends__charts">
              <section
                className="cp-chart-card"
                aria-labelledby="suds-trend-heading"
              >
                <div className="cp-chart-card__header">
                  <div>
                    <h3 id="suds-trend-heading">
                      SUDS Trend Line: Before vs. After
                    </h3>
                    <p>
                      Daily averages use completed activities with recorded
                      SUDS.
                    </p>
                  </div>
                </div>
                <SudsTrendChart points={sudsPoints} />
              </section>

              <section
                className="cp-chart-card"
                aria-labelledby="daily-mood-trend-heading"
              >
                <div className="cp-chart-card__header">
                  <div>
                    <h3 id="daily-mood-trend-heading">
                      Daily Mood Check Trend Line
                    </h3>
                    <p>
                      Daily averages compare symptom ratings with overall mood.
                    </p>
                  </div>
                </div>
                {moodState.status === "loading" ? (
                  <LoadingState label="Loading daily mood trends" />
                ) : null}
                {moodState.status === "error" ? (
                  <ErrorState
                    error={moodState.error}
                    onRetry={moodState.refresh}
                  />
                ) : null}
                {moodData && !moodAvailable ? (
                  <ErrorState
                    error={new Error("Daily mood trends are not available")}
                  />
                ) : null}
                {moodAvailable ? (
                  <DailyMoodTrendChart points={moodPoints} />
                ) : null}
              </section>

              <section
                className="cp-chart-card"
                aria-labelledby="youth-top-problems-trend-heading"
              >
                <div className="cp-chart-card__header">
                  <div>
                    <h3 id="youth-top-problems-trend-heading">
                      Youth Top Problems for Client
                    </h3>
                    <p>
                      Daily averages show how the client&apos;s three reported
                      problem scores change over time.
                    </p>
                  </div>
                </div>
                {youthTopProblemsState.status === "loading" ? (
                  <LoadingState label="Loading Youth Top Problems trends" />
                ) : null}
                {youthTopProblemsState.status === "error" ? (
                  <ErrorState
                    error={youthTopProblemsState.error}
                    onRetry={youthTopProblemsState.refresh}
                  />
                ) : null}
                {youthTopProblemsData && !youthTopProblemsAvailable ? (
                  <ErrorState
                    error={
                      new Error(
                        "Youth Top Problems trends are not available",
                      )
                    }
                  />
                ) : null}
                {youthTopProblemsAvailable ? (
                  <YouthTopProblemsTrendChart
                    points={youthTopProblemsPoints}
                  />
                ) : null}
              </section>
            </div>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
